<script lang="ts" setup>
import { updateSalesLead } from '@/api/calls/directus-calls/sales-leads-calls'
import type SalesLead from '@/classes/sales_classes/salesLead'
import { MutationTypes, type State } from '@/store/store_types'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import ContactSalesLeadAlert from '../alert-popup/ContactSalesLeadAlert.vue'
import DashboardBreadcrumbs, { type BreadcrumbsItem } from '../dashboard/DashboardBreadcrumbs.vue'
import SalesLeads from './SalesLeads.vue'
import SalesLeadsDetails from './SalesLeadsDetails.vue'

const { mdAndUp } = useDisplay()
const store = useStore<State>()

const page = ref<'leads' | 'details'>('leads')
const displayedLead = ref<SalesLead | undefined>(undefined)

const showAlert = ref(false)
const DSA_contact_sales_lead_warning = computed(() => store.state.DSA_contact_sales_lead_warning)
const processing = ref(false)

function followUpPreCheck(lead: SalesLead) {
  // keep context of lead
  displayedLead.value = lead
  if (DSA_contact_sales_lead_warning.value) {
    // skip warning
    followUp(lead)
  } else {
    // show warning
    showAlert.value = true
  }
}
async function followUp(lead?: SalesLead) {
  const leadToFollowUp = lead ?? displayedLead.value
  if (
    leadToFollowUp &&
    leadToFollowUp.external_id &&
    store.state.user &&
    store.state.user.directusId
  ) {
    processing.value = true
    window.location.href = 'mailto:' + leadToFollowUp.user.email
    const res = await updateSalesLead(leadToFollowUp.external_id, {
      contacted: true,
      contacted_on: new Date().toISOString(),
      contacted_by: store.state.user.directusId,
    })
    if (res) {
      leadToFollowUp.contacted = true
      leadToFollowUp.contactedBy = store.state.user
      leadToFollowUp.contactedDate = new Date()
      store.commit(MutationTypes.updateIndividualSalesLeads, leadToFollowUp)
    } else {
      // display error message
    }
    processing.value = false
  }
}

const BreadcrumbsItems = computed(() => {
  if (page.value === 'leads') {
    return [{ text: 'Sales leads', disabled: true }]
  } else {
    return [
      { text: 'Sales leads', disabled: false },
      { text: 'Enquiry details', disabled: true },
    ]
  }
})

function breadcrumbNavigation(item: BreadcrumbsItem) {
  if (item.disabled) return
  if (item.text === 'Manage account') {
    page.value = 'leads'
    displayedLead.value = undefined
    store.commit(MutationTypes.setDashboardDialogContent, 'account')
  } else if (item.text === 'Sales leads') {
    // handled sales leads in BreadcrumbsItems
    page.value = 'leads'
    displayedLead.value = undefined
  } else if (item.text === 'Enquiry details') {
    // handled Enquiry details in BreadcrumbsItems
    page.value = 'details'
  }
}

function back() {
  if (page.value === 'leads') {
    breadcrumbNavigation({ text: 'Manage account' })
  } else if (page.value === 'details') {
    page.value = 'leads'
    displayedLead.value = undefined
  }
}
</script>

<template>
  <DashboardBreadcrumbs
    v-if="mdAndUp"
    :items="BreadcrumbsItems"
    @navigation="breadcrumbNavigation"
    @back="back"
  />
  <SalesLeads
    v-if="page === 'leads'"
    @view-lead="
      ($event) => {
        displayedLead = $event
        page = 'details'
      }
    "
    @contact-lead="followUpPreCheck"
  />
  <SalesLeadsDetails
    v-else
    :lead="(displayedLead as SalesLead | undefined)"
    @contact-lead="followUpPreCheck"
    @close="
      () => {
        page = 'leads'
        displayedLead = undefined
      }
    "
  />
  <ContactSalesLeadAlert 
    :activator="showAlert" 
    @close="showAlert = false" 
    @continue="() => {
      followUp()
      showAlert = false
    }"
  />
</template>
